import { useCallback, useEffect, useState } from 'react';
import { Server, AlertCircle, MoreVertical, Cloud, Cpu, HardDrive, CheckCircle, XCircle, Database } from 'lucide-react';
import { LastSynced } from '../components/ui/LastSynced';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, type ManagedClusterKind } from '../lib/api-client';
import { getTenantColor, getCloudProviderColor } from '../lib/colors';
import { TenantHeading } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';
import { openMasLogin } from '../lib/utils';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { ClusterStatusReport } from '../components/ClusterStatusReport';
import { ManageSecretsDialog } from '../components/openshift/ManageSecretsDialog';
import { LogsEventsDialog } from '../components/openshift/LogsEventsDialog';
import { UptimeScheduleDialog } from '../components/openshift/UptimeScheduleDialog';
import { usePermission } from '../hooks/usePermission';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { PageHeader } from '../components/ui/PageHeader';
import { ToastContainer } from '../components/ui/Toast';
import { buildOpenShiftConsoleUrl } from '../lib/cluster-login';

interface OpenshiftCluster {
  instanceName: string;
  tenantName: string;
  tenantCode: string;
  cloudProvider: string;
  apiUrl: string;
  status: 'checking' | 'running' | 'stopped' | 'unknown';
  kubeconfig: boolean;
  serverInventory?: {
    capturedAt: string;
    totalNodes: number;
    mappedServers: number;
    workerNodes: number;
    masterNodes: number;
    statefulNodes?: number;
  } | null;
}

interface TenantClusters {
  tenant: string;
  tenantName: string;
  tenantColor?: string;
  providers: {
    aws: OpenshiftCluster[];
    oci: OpenshiftCluster[];
    hetzner: OpenshiftCluster[];
  };
}

function isProductionCluster(instanceName: string): boolean {
  return /\b(prd|prod)\b/i.test(instanceName);
}


interface ClusterCardProps {
  cluster: OpenshiftCluster;
  kind: ManagedClusterKind;
  canView: boolean;
  canManageServer: boolean;
  onStatusReport: (c: OpenshiftCluster) => void;
  onManageSecrets: (c: OpenshiftCluster) => void;
  onLogsEvents: (c: OpenshiftCluster) => void;
  onUptimeSchedule: (c: OpenshiftCluster) => void;
  onPowerAction: (c: OpenshiftCluster, action: 'start' | 'stop') => void;
}

function ClusterCard({
  cluster,
  kind,
  canView,
  canManageServer,
  onStatusReport,
  onManageSecrets,
  onLogsEvents,
  onUptimeSchedule,
  onPowerAction,
}: ClusterCardProps) {
  const getStatusBadge = (status: string) => {
    if (status === 'checking') return <Badge variant="outline" className="bg-gray-100 text-gray-600 text-xs">Checking...</Badge>;
    if (status === 'running') return <Badge variant="success" className="text-xs">Running</Badge>;
    if (status === 'stopped') return <Badge variant="destructive" className="text-xs">Stopped</Badge>;
    return <Badge variant="outline" className="text-xs">Unknown</Badge>;
  };

  const inv = cluster.serverInventory;

  return (
    <Card className="hover:shadow-lg transition-shadow w-full max-w-full overflow-visible">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <Server className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base truncate">{cluster.instanceName}</CardTitle>
              <CardDescription className="mt-0.5 text-xs truncate">{cluster.apiUrl}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {getStatusBadge(cluster.status)}
            {(cluster.status === 'running' || cluster.status === 'stopped') && (canView || canManageServer) && (
              <DropdownMenu
                trigger={
                  <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                    <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </button>
                }
                items={[
                  ...(canView && kind === 'openshift' ? [{
                    label: 'MAS Login',
                    onClick: () => void openMasLogin({
                      instanceName: cluster.instanceName,
                      clusterName: cluster.instanceName,
                      tenantCode: cluster.tenantCode,
                      cloudProvider: cluster.cloudProvider,
                    }),
                  }] : []),
                  ...(canView && kind === 'openshift' ? [{
                    label: 'OpenShift Login',
                    onClick: () => window.open(
                      buildOpenShiftConsoleUrl(cluster.apiUrl),
                      '_blank',
                    ),
                  }] : []),
                  ...(canView ? [{
                    label: 'Status Report',
                    onClick: () => onStatusReport(cluster),
                  }] : []),
                  ...(canManageServer && cluster.status === 'running' && !isProductionCluster(cluster.instanceName) ? [{
                    label: 'Stop Cluster',
                    onClick: () => onPowerAction(cluster, 'stop'),
                  }] : []),
                  ...(canManageServer && cluster.status === 'stopped' ? [{
                    label: 'Start Cluster',
                    onClick: () => onPowerAction(cluster, 'start'),
                  }] : []),
                  ...(canManageServer && !isProductionCluster(cluster.instanceName) ? [{
                    label: 'Uptime Schedule',
                    onClick: () => onUptimeSchedule(cluster),
                  }] : []),
                  ...(canManageServer ? [{
                    label: 'Manage Secrets',
                    onClick: () => onManageSecrets(cluster),
                  }] : []),
                  ...(canManageServer ? [{
                    label: 'Manage',
                    onClick: () => onLogsEvents(cluster),
                  }] : []),
                ]}
                align="end"
              />
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 pb-3">
        <div className="space-y-1.5 text-xs">
          {/* Node breakdown from inventory */}
          {inv ? (
            <div className="flex items-center gap-3 pt-1">
              {kind === 'openshift' && (
                <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                  <Cpu className="w-3 h-3 text-purple-500" />
                  <span className="font-medium">{inv.masterNodes}</span>
                  <span className="text-gray-500">master{inv.masterNodes !== 1 ? 's' : ''}</span>
                </div>
              )}
              <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                <HardDrive className="w-3 h-3 text-blue-500" />
                <span className="font-medium">{inv.workerNodes}</span>
                <span className="text-gray-500">worker{inv.workerNodes !== 1 ? 's' : ''}</span>
              </div>
              {(inv.statefulNodes || 0) > 0 && (
                <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                  <Database className="w-3 h-3 text-emerald-500" />
                  <span className="font-medium">{inv.statefulNodes}</span>
                  <span className="text-gray-500">stateful</span>
                </div>
              )}
              {inv.mappedServers < inv.totalNodes && (
                <span className="text-amber-600 dark:text-amber-400">
                  ({inv.totalNodes - inv.mappedServers} unmapped)
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1 pt-1 text-gray-400 dark:text-gray-500">
              <HardDrive className="w-3 h-3" />
              <span>No node inventory</span>
            </div>
          )}

          {/* Divider + meta row */}
          <div className="flex items-center justify-between pt-1.5 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
              {cluster.kubeconfig ? (
                <CheckCircle className="w-3 h-3 text-green-500" />
              ) : (
                <XCircle className="w-3 h-3 text-red-400" />
              )}
              <span>Kubeconfig</span>
            </div>
            <LastSynced iso={inv?.capturedAt} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProviderSection({
  provider,
  clusters,
  kind,
  canView,
  canManageServer,
  onStatusReport,
  onManageSecrets,
  onLogsEvents,
  onUptimeSchedule,
  onPowerAction,
}: {
  provider: string;
  clusters: OpenshiftCluster[];
  kind: ManagedClusterKind;
  canView: boolean;
  canManageServer: boolean;
  onStatusReport: (c: OpenshiftCluster) => void;
  onManageSecrets: (c: OpenshiftCluster) => void;
  onLogsEvents: (c: OpenshiftCluster) => void;
  onUptimeSchedule: (c: OpenshiftCluster) => void;
  onPowerAction: (c: OpenshiftCluster, action: 'start' | 'stop') => void;
}) {
  if (clusters.length === 0) return null;
  return (
    <div className="space-y-4 md:ml-6">
      <div className="flex items-center gap-2 py-2 border-b border-gray-200 dark:border-gray-700">
        <Cloud className="w-5 h-5" />
        <h3 className="text-base font-medium" style={{ color: getCloudProviderColor(provider as 'aws' | 'oci' | 'hetzner') }}>
          {provider.toUpperCase()}
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">({clusters.length})</span>
      </div>
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 overflow-visible">
        {clusters.map((cluster) => (
          <ClusterCard
            key={cluster.instanceName}
            cluster={cluster}
            kind={kind}
            canView={canView}
            canManageServer={canManageServer}
            onStatusReport={onStatusReport}
            onManageSecrets={onManageSecrets}
            onLogsEvents={onLogsEvents}
            onUptimeSchedule={onUptimeSchedule}
            onPowerAction={onPowerAction}
          />
        ))}
      </div>
    </div>
  );
}

export default function OpenshiftClusters({ kind = 'openshift' }: { kind?: ManagedClusterKind }) {
  const { accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('openshift_clusters', 'read');
  const tenantMetaMap = useTenantMetaMap(canView);
  const { isAllowed: canManageServer } = usePermission('openshift_clusters', 'manage_server');
  const [tenantClusters, setTenantClusters] = useState<TenantClusters[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCluster, setSelectedCluster] = useState<OpenshiftCluster | null>(null);
  const [showStatusReport, setShowStatusReport] = useState(false);
  const [showManageSecrets, setShowManageSecrets] = useState(false);
  const [showLogsEvents, setShowLogsEvents] = useState(false);
  const [showUptimeSchedule, setShowUptimeSchedule] = useState(false);
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: 'success' | 'error' | 'warning' | 'info' }>>([]);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Math.random().toString(36).slice(2);
    setToasts(prev => [...prev, { id, message, type }]);
  };
  const removeToast = (id: string) => setToasts(prev => prev.filter(t => t.id !== id));

  const tenantsToQuery = selectedTenants.length > 0 ? selectedTenants : accessibleTenants;

  useActivityHeartbeat(kind === 'kubernetes' ? 'cache:kubernetes_clusters' : 'cache:openshift_clusters', canView);

  const fetchClusters = useCallback(async () => {
    if (tenantsToQuery.length === 0) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const tenantsResponse = await apiClient.getTenants();
      const tenantsData = Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || [];
      const tenantMap = new Map<string, { name: string; color?: string }>(
        tenantsData.map((t: any) => [t.code.toLowerCase(), { name: t.name, color: t.color }]),
      );

      const response = await apiClient.getOpenshiftClusters(kind);
      const clusters: OpenshiftCluster[] = response.clusters || [];
      setSyncedAt(response.syncedAt ?? null);

      const groupedByTenant = new Map<string, TenantClusters>();
      clusters.forEach((cluster) => {
        const tenantLower = cluster.tenantCode.toLowerCase();
        if (tenantsToQuery.length > 0 && !tenantsToQuery.some(t => t.toLowerCase() === tenantLower)) return;
        const tenantInfo = tenantMap.get(tenantLower);
        if (!groupedByTenant.has(tenantLower)) {
          groupedByTenant.set(tenantLower, {
            tenant: cluster.tenantCode,
            tenantName: tenantInfo?.name || cluster.tenantName,
            tenantColor: tenantInfo?.color,
            providers: { aws: [], oci: [], hetzner: [] },
          });
        }
        const tenantData = groupedByTenant.get(tenantLower)!;
        const provider = cluster.cloudProvider.toLowerCase() as 'aws' | 'oci' | 'hetzner';
        if (provider in tenantData.providers) tenantData.providers[provider].push(cluster);
      });

      setTenantClusters(Array.from(groupedByTenant.values()));
    } catch (err: any) {
      console.error('Failed to fetch clusters:', err);
      setError(err.message || `Failed to load ${kind === 'kubernetes' ? 'Kubernetes' : 'Openshift'} clusters`);
    } finally {
      setIsLoading(false);
    }
  }, [tenantsToQuery.join(','), kind]);

  useEffect(() => { void fetchClusters(); }, [fetchClusters]);

  useCacheEvents({
    enabled: canView,
    cacheNames: [kind === 'kubernetes' ? 'kubernetes_clusters_page' : 'openshift_clusters_page'],
    onEvent: () => { void fetchClusters(); },
  });

  const triggerPowerWorkflow = async (cluster: OpenshiftCluster, action: 'start' | 'stop') => {
    try {
      const result = action === 'start'
        ? await apiClient.startOpenshiftCluster(cluster.tenantCode, cluster.instanceName, cluster.cloudProvider, kind)
        : await apiClient.stopOpenshiftCluster(cluster.tenantCode, cluster.instanceName, cluster.cloudProvider, kind);
      addToast(`Workflow started: ${result.workflowName}`, 'success');
      void fetchClusters();
    } catch (err: any) {
      addToast(err?.message || `Failed to ${action} cluster`, 'error');
    }
  };

  const handleStatusReport = (c: OpenshiftCluster) => { setSelectedCluster(c); setShowStatusReport(true); };
  const handleManageSecrets = (c: OpenshiftCluster) => { setSelectedCluster(c); setShowManageSecrets(true); };
  const handleLogsEvents = (c: OpenshiftCluster) => { setSelectedCluster(c); setShowLogsEvents(true); };
  const handleUptimeSchedule = (c: OpenshiftCluster) => { setSelectedCluster(c); setShowUptimeSchedule(true); };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2].map((i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="h-6 w-32" />
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((j) => (
                <Card key={j}>
                  <CardHeader>
                    <Skeleton className="h-5 w-40 mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-16 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="py-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-600 dark:text-red-400 mb-4" />
            <p className="text-red-600 dark:text-red-400">Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalClusters = tenantClusters.reduce(
    (sum, tc) => sum + tc.providers.oci.length + tc.providers.hetzner.length + tc.providers.aws.length, 0,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <PageHeader
          icon={Cloud}
          iconColor="text-orange-600 dark:text-orange-400"
          iconBgColor="bg-orange-100 dark:bg-orange-900/30"
          description={kind === 'kubernetes' ? 'Monitor and manage tenant Kubernetes clusters' : 'Monitor and manage tenant OpenShift clusters'}
          count={totalClusters}
          countLabel="clusters"
          countColor="text-orange-600 dark:text-orange-400"
        />
        <LastSynced iso={syncedAt} label="Last refreshed" />
      </div>

      {tenantClusters.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
            No {kind === 'kubernetes' ? 'Kubernetes' : 'Openshift'} clusters found for the selected tenants.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {tenantClusters.map((tenantData) => {
            const tenantTotal = tenantData.providers.oci.length + tenantData.providers.hetzner.length + tenantData.providers.aws.length;
            if (tenantTotal === 0) return null;
            return (
              <div key={tenantData.tenant} className="space-y-6">
                <div className="flex items-center justify-between">
                  <TenantHeading
                    tenant={resolveTenantMeta(tenantMetaMap, tenantData.tenant, {
                      name: tenantData.tenantName,
                      color: tenantData.tenantColor,
                    })}
                    title={tenantData.tenantName || tenantData.tenant}
                  />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {tenantTotal} cluster{tenantTotal !== 1 ? 's' : ''}
                  </span>
                </div>

                {(['oci', 'hetzner', 'aws'] as const).map((p) => (
                  <ProviderSection
                    key={p}
                    provider={p}
                    clusters={tenantData.providers[p]}
                    kind={kind}
                    canView={canView}
                    canManageServer={canManageServer}
                    onStatusReport={handleStatusReport}
                    onManageSecrets={handleManageSecrets}
                    onLogsEvents={handleLogsEvents}
                    onUptimeSchedule={handleUptimeSchedule}
                    onPowerAction={triggerPowerWorkflow}
                  />
                ))}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showStatusReport} onOpenChange={setShowStatusReport}>
        <DialogContent className="w-full md:w-[50vw] md:max-w-[50vw] h-[90vh]">
          <DialogHeader className="px-6 pt-8 pb-6 border-b border-gray-200 dark:border-gray-700">
            <DialogTitle>
              {selectedCluster ? `Cluster Status Report - ${selectedCluster.instanceName}` : 'Cluster Status Report'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="px-6 py-4">
            {selectedCluster && (
              <ClusterStatusReport
                key={`${selectedCluster.tenantCode}-${selectedCluster.instanceName}-${selectedCluster.cloudProvider}-${kind}`}
                tenantCode={selectedCluster.tenantCode}
                instanceName={selectedCluster.instanceName}
                cloudProvider={selectedCluster.cloudProvider}
                clusterKind={kind}
              />
            )}
          </DialogBody>
          <DialogFooter className="px-6 py-4">
            <Button variant="secondary" onClick={() => setShowStatusReport(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedCluster && canManageServer && (
        <ManageSecretsDialog
          open={showManageSecrets}
          onClose={() => setShowManageSecrets(false)}
          tenantCode={selectedCluster.tenantCode}
          instanceName={selectedCluster.instanceName}
          cloudProvider={selectedCluster.cloudProvider}
          clusterKind={kind}
        />
      )}

      {selectedCluster && canManageServer && (
        <LogsEventsDialog
          open={showLogsEvents}
          onClose={() => setShowLogsEvents(false)}
          tenantCode={selectedCluster.tenantCode}
          instanceName={selectedCluster.instanceName}
          cloudProvider={selectedCluster.cloudProvider}
          clusterKind={kind}
        />
      )}

      <UptimeScheduleDialog
        open={showUptimeSchedule}
        onClose={() => setShowUptimeSchedule(false)}
        cluster={selectedCluster ? {
          tenantCode: selectedCluster.tenantCode,
          instanceName: selectedCluster.instanceName,
          cloudProvider: selectedCluster.cloudProvider,
        } : null}
        clusterKind={kind}
      />
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
